import { axios, transformAxiosError } from "../utils/axios.create";
import {
  CreateClientPayload,
  CreateClientResponse,
  earningsResponse,
  SearchClientsPayload,
  SearchClientsResponse,
  SearchEarningPayload,
  SearchEarningResponse,
  SearchRedemptionPayload,
  SearchRedemptionResponse,
} from "@/domain/client.interface";

const findAll = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
  programId,
  orgUnitId,
  userId,
}: SearchClientsPayload): Promise<SearchClientsResponse> => {
  console.log("findAll", {
    sortField,
    sortOrder,
    pageNumber,
    pageSize,
    search,
    status,
    programId,
    orgUnitId,
    userId,
  });
  if (!programId) {
    return {
      participants: [],
      totalCount: 0,
    };
  }

  const queryParams = new URLSearchParams({
    status: status.join(","),
    search,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
    programId,
    userId,
  });

  if (orgUnitId) {
    queryParams.append("orgUnitId", orgUnitId);
  }

  return axios
    .get(`/participants/search?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        participants: body.participants,
        totalCount: body.totalCount,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (
  data: CreateClientPayload
): Promise<CreateClientResponse> => {
  return axios
    .post("/participants", data)
    .then((res) => res.data.body)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const update = async ({
  participantId,
  enrollmentId,
  data,
}: {
  participantId: string;
  enrollmentId: string;
  data: CreateClientPayload;
}): Promise<CreateClientResponse> => {
  return axios
    .put(`/participants/${participantId}/enrollment/${enrollmentId}`, data)
    .then((res) => res.data.body)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

/**
 * PRET-1837 -- read a participant's enrolment so a form can show the treatment allocation it
 * no longer lets anyone edit. The API returns the whole enrolment under lastEnrollment, so the
 * allocation comes through without a dedicated endpoint.
 */
const getEnrollment = async ({
  participantId,
  enrollmentId,
}: {
  participantId: string;
  enrollmentId: string;
}): Promise<{
  lastEnrollment?: {
    treatmentAgencyId?: string;
    locationId?: string;
    // Bounds the appointment date pickers: eligibility starts the day AFTER release.
    releaseDate?: string;
  };
}> => {
  return axios
    .get(`/participants/${participantId}/enrollment/${enrollmentId}`)
    .then((res) => res.data.body)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const dismissed = async ({
  participantId,
  enrollmentId,
  updatedBy,
}: {
  participantId: string;
  enrollmentId: string;
  updatedBy: string;
}): Promise<void> => {
  return axios
    .put(
      `/participants/${participantId}/enrollment/disenroll/${enrollmentId}`,
      {
        updatedBy,
      }
    )
    .then((res) => res.data.body)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const findAllEarnings = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
  programId,
  enrollmentId,
}: SearchEarningPayload): Promise<SearchEarningResponse> => {
  const queryParams = new URLSearchParams({
    search,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
    programId,
    enrollmentId,
    mechanism: status.join(","),
  });

  return axios
    .get(
      `/enrollments/${enrollmentId}/rewards-earned/search?${queryParams.toString()}`
    )
    .then((res) => {
      const body = res.data.body;
      return {
        totalCount: body.totalCount,
        earnings: body.list.map((item: earningsResponse) => {
          return {
            description: item.description,
            mechanism: mapMechanismToReadable(item.mechanism),
            date: item.date,
            points: item.points,
          };
        }),
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findAllRedemptions = async ({
  accountId: _accountId,
  enrollmentId,
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  mechanism,
}: SearchRedemptionPayload): Promise<SearchRedemptionResponse> => {
  const queryParams = new URLSearchParams({
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
  });

  if (mechanism) {
    queryParams.append("mechanism", mechanism);
  }

  return axios
    .get(
      `/enrollments/${enrollmentId}/rewards-redeemed/search?${queryParams.toString()}`
    )
    .then((res) => res.data.body)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export const ClientsService = {
  findAll,
  create,
  update,
  dismissed,
  getEnrollment,
  findAllEarnings,
  findAllRedemptions,
};

const mapMechanismToReadable = (mechanism: string): string => {
  const labelMap: Record<string, string> = {
    ACCOUNT_CREATION: "Account creation",
    APPOINTMENT_CREATION: "Appointment creation",
    CHECK_IN_VALIDATED: "Appointment check-in",
    APPOINTMENT_MISSED_REASON: "Appointment missed reason",
    SUPERVISION_ACTIVITY: "Contextual agent",
    MANUAL_REIMBURSEMENT: "Manual reimbursement",
    GOAL_COMPLETION: "Goal completions",
    STEP_COMPLETION: "Step completions",
    JOURNAL: "Journals",
    SUPERVISION_ACTIVITIES: "Supervision activities",
    IN_HOUSE_SERVICES: "In-house services",
    EXTERNAL_PROVIDER_SERVICES: "External provider services",
    CONTEXTUAL_AGENT: "Contextual agent",
  };

  return labelMap[mechanism] || mechanism;
};
